import { useMemo } from 'react'
import type { FieldStringProps as StringFieldProps } from '../String'
import StringField from '../String'
import useTranslation from '../../hooks/useTranslation'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

export type FieldEmailProps = StringFieldProps

// Allow Unicode characters in local part (before @)
const aText = `[A-Za-z0-9!#$%&'*+/=?^_\\x60{|}~\\u0080-\\uFFFF-]+`
export const pattern =
  `^${aText}(?:\\.${aText})*@` + // Local part: RFC 5322 aText segments separated by dots, plus Unicode
  `(?:` +
  // Multi-label domain ending with a TLD (e.g. example.com, sub.example.co.uk)
  `(?:` +
  `(?:xn--[A-Za-z0-9-]+|[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*)\\.)+` +
  `(?:[A-Za-z]{2,}|xn--[A-Za-z0-9-]{2,})` +
  `|` +
  // Single-label TLDs (only punycode, e.g. xn--p1ai)
  `(?:xn--[A-Za-z0-9-]{2,})` +
  `|` +
  // IP literals
  `\\[(?:` +
  `\\d{1,3}(?:\\.\\d{1,3}){3}` + // IPv4 literal (basic format only)
  `|` +
  `IPv6:[0-9A-Fa-f:]+` + // IPv6 literal (basic format only)
  `)\\]` +
  `)$`

function Email(props: FieldEmailProps) {
  const { label, errorRequired, errorPattern } = useTranslation().Email

  const errorMessages = useMemo(
    () => ({
      'Field.errorRequired': errorRequired,
      'Field.errorPattern': errorPattern,
      ...props.errorMessages,
    }),
    [errorPattern, errorRequired, props.errorMessages]
  )

  const StringFieldProps: FieldEmailProps = {
    label,
    autoComplete: 'email',
    inputMode: 'email',
    // RFC 5321, which is used in DNB, limits the total length of an email address to 254 characters
    maxLength: 254,
    pattern,
    trim: true,
    ...props,
    errorMessages,
  }

  return <StringField {...StringFieldProps} />
}

withComponentMarkers(Email, { _supportsSpacingProps: true })

export default Email
